import { useMemo } from "react";

import { MapPin, Package, Pencil, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import type { Position, RackInventoryData } from "~/types/wms";
import { RACK_TYPE_LABELS } from "~/types/wms";

import { PositionInventoryCard } from "./PositionInventoryCard";

interface RackDetailPanelProps {
  rackData: RackInventoryData | null;
  selectedPositionId: string | null;
  onPositionClick: (positionId: string) => void;
  onClosePositionDetail: () => void;
  onEditRack?: () => void;
  onDeleteRack?: () => void;
  onEditPosition?: (position: Position) => void;
  onDeletePosition?: (position: Position) => void;
  isLoading?: boolean;
}

type PositionStatus = "empty" | "occupied" | "partial" | "reserved";

function getPositionStatus(
  pos: RackInventoryData["positions"][0]
): PositionStatus {
  if (!pos.hasStock) {
    return "empty";
  }
  if (pos.reservedBoxes >= pos.totalBoxes) {
    return "reserved";
  }
  if (pos.reservedBoxes > 0) {
    return "partial";
  }
  return "occupied";
}

function getStatusColor(status: PositionStatus): string {
  switch (status) {
    case "empty":
      return "bg-gray-100 hover:bg-gray-200 border-gray-300";
    case "occupied":
      return "bg-green-100 hover:bg-green-200 border-green-400";
    case "partial":
      return "bg-yellow-100 hover:bg-yellow-200 border-yellow-400";
    case "reserved":
      return "bg-blue-100 hover:bg-blue-200 border-blue-400";
  }
}

function getStatusTextColor(status: PositionStatus): string {
  switch (status) {
    case "empty":
      return "text-gray-500";
    case "occupied":
      return "text-green-700";
    case "partial":
      return "text-yellow-700";
    case "reserved":
      return "text-blue-700";
  }
}

export function RackDetailPanel({
  rackData,
  selectedPositionId,
  onPositionClick,
  onClosePositionDetail,
  onEditRack,
  onDeleteRack,
  onEditPosition,
  onDeletePosition,
  isLoading,
}: RackDetailPanelProps) {
  // Sort positions by level (highest first) for vertical display
  const sortedPositions = useMemo(() => {
    if (!rackData?.positions || rackData.positions.length === 0) {
      return [];
    }

    // Sort by level descending (top to bottom)
    return [...rackData.positions].sort((a, b) => {
      const levelA = a.position.level || 0;
      const levelB = b.position.level || 0;
      return levelB - levelA;
    });
  }, [rackData?.positions]);

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4" />
            Detalle del Rack
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!rackData) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4" />
            Detalle del Rack
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-10 w-10 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              Selecciona un rack para ver su detalle
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const occupancy = rackData.occupancyPercent;

  return (
    <Card className="flex h-full flex-col">
      {/* Header with rack info */}
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              {rackData.rack.full_code}
              {(onEditRack || onDeleteRack) && (
                <div className="ml-2 flex items-center gap-1">
                  {onEditRack && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={onEditRack}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                  {onDeleteRack && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={onDeleteRack}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  )}
                </div>
              )}
            </CardTitle>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {RACK_TYPE_LABELS[rackData.rack.rack_type]}
              </Badge>
              {rackData.rack.barcode && (
                <span className="font-mono text-xs text-muted-foreground">
                  {rackData.rack.barcode}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{occupancy.toFixed(0)}%</div>
            <div className="text-xs text-muted-foreground">ocupación</div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Progress value={occupancy} className="h-2 flex-1" />
        </div>

        <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">
              {rackData.totalPositions}
            </span>{" "}
            posiciones
          </span>
          <span>
            <span className="font-medium text-foreground">
              {rackData.occupiedPositions}
            </span>{" "}
            ocupadas
          </span>
          <span>
            <span className="font-medium text-foreground">
              {rackData.totalPositions - rackData.occupiedPositions}
            </span>{" "}
            vacías
          </span>
        </div>
      </CardHeader>

      {/* Scrollable content */}
      <CardContent className="flex-1 space-y-4 overflow-y-auto">
        {/* Legend */}
        <div className="flex items-center gap-4 border-b pb-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-gray-300 bg-gray-100" />
            <span className="text-muted-foreground">Vacío</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-green-400 bg-green-100" />
            <span className="text-muted-foreground">Ocupado</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-yellow-400 bg-yellow-100" />
            <span className="text-muted-foreground">Parcial</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded border border-blue-400 bg-blue-100" />
            <span className="text-muted-foreground">Reservado</span>
          </div>
        </div>

        {/* Position Grid */}
        {sortedPositions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <Package className="mb-2 h-8 w-8 opacity-50" />
            <p>No hay posiciones definidas para este rack</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedPositions.map((posData) => {
              const status = getPositionStatus(posData);
              const isSelected = selectedPositionId === posData.position.id;

              return (
                <div
                  key={posData.position.id}
                  className="flex items-stretch gap-2"
                >
                  {/* Level label */}
                  <div className="flex w-14 shrink-0 items-center justify-center rounded bg-slate-100 px-2 text-xs font-medium text-slate-600">
                    N{posData.position.level}
                  </div>
                  {/* Position cell */}
                  <button
                    type="button"
                    onClick={() => onPositionClick(posData.position.id)}
                    className={cn(
                      "flex h-12 flex-1 items-center justify-between rounded border-2 px-4 transition-all",
                      getStatusColor(status),
                      isSelected && "ring-2 ring-primary ring-offset-1"
                    )}
                  >
                    <span
                      className={cn(
                        "text-sm font-medium",
                        getStatusTextColor(status)
                      )}
                    >
                      {posData.position.code}
                    </span>
                    {posData.hasStock ? (
                      <span
                        className={cn("text-sm", getStatusTextColor(status))}
                      >
                        {posData.availableBoxes}/{posData.totalBoxes} cajas
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">vacío</span>
                    )}
                  </button>
                  {/* Edit/Delete buttons */}
                  {(onEditPosition || onDeletePosition) && (
                    <div className="flex items-center gap-0.5">
                      {onEditPosition && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onEditPosition(posData.position)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      {onDeletePosition && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onDeletePosition(posData.position)}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Position detail */}
        {selectedPositionId && (
          <div className="border-t pt-4">
            <PositionInventoryCard
              positionId={selectedPositionId}
              onClose={onClosePositionDetail}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
